/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import {
  FileSignature, ShieldCheck, Lock, Download, RefreshCw, CheckCircle2, Fingerprint, Trash2, CloudUpload,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { useBrand } from '@/lib/whitelabel';
import { SignaturePad } from '@/components/consent/SignaturePad';
import { PatientPicker } from '@/components/voicediary/PatientPicker';
import { OfflineStatusBadge } from '@/components/clinical/OfflineStatusBadge';
import { getConsentTemplates, listFeaEvidence } from '@/lib/consents';
import { getDeviceKey, aesEncrypt, aesDecrypt, sha256Hex, downloadBuffer } from '@/lib/cryptoLocal';
import { putRecord, getAll, deleteRecord, queueOutbox, STORES, uuid, getMeta, setMeta } from '@/lib/offlineDb';
import { syncEngine } from '@/lib/syncEngine';

/**
 * ConsentPage — Consenso informato con Firma Elettronica Avanzata (FEA) su tablet.
 *
 * Flusso:
 *   1. Scegli modello + paziente + prestazione → anteprima testo
 *   2. Il paziente firma sul tablet (SignaturePad, Pointer Events)
 *   3. [Firma e archivia] →
 *        a) PDF generato nel browser (jsPDF) con testo, firma, hash, timestamp
 *        b) SHA-256 del PDF (impronta FEA)
 *        c) PDF CIFRATO AES-GCM con la chiave del dispositivo → IndexedDB
 *        d) Evidenza (hash + metadati, MAI il PDF in chiaro) → server via outbox
 *   4. Elenco locale: scarica (decifra al volo) / elimina
 */

const TEMPLATE_CACHE = 'consent_templates';

const fill = (tpl, vars) => tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] || '________');

const buildConsentPdf = ({ title, body, patientName, taxCode, doctorName, clinicName, procedure, signatureDataUrl, signedAt, evidence }) => {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const mx = 48; const w = doc.internal.pageSize.getWidth() - mx * 2; let y = 60;
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.text(clinicName || 'Clinica', mx, y); y += 22;
  doc.setFontSize(13); doc.text(title, mx, y); y += 20;
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10);
  doc.text(`Paziente: ${patientName}${taxCode ? `  ·  CF: ${taxCode}` : ''}`, mx, y); y += 14;
  doc.text(`Operatore: ${doctorName || '—'}   ·   Prestazione: ${procedure || '—'}`, mx, y); y += 20;
  const lines = doc.splitTextToSize(body, w);
  doc.setFontSize(10.5);
  lines.forEach((ln) => { if (y > 720) { doc.addPage(); y = 60; } doc.text(ln, mx, y); y += 14; });
  y += 18;
  if (y > 600) { doc.addPage(); y = 60; }
  doc.setFontSize(10); doc.text(`Luogo e data: ${new Date(signedAt).toLocaleString('it-IT')}`, mx, y); y += 10;
  doc.setDrawColor(160); doc.rect(mx, y, 240, 90);
  if (signatureDataUrl) doc.addImage(signatureDataUrl, 'PNG', mx + 6, y + 6, 228, 78);
  doc.setFontSize(8); doc.text('Firma del paziente (FEA — DPCM 22/02/2013)', mx, y + 102);
  y += 120;
  doc.setFontSize(7.5); doc.setTextColor(110);
  doc.text(`Evidenze FEA: tratti=${evidence.strokes} punti=${evidence.points} durata=${evidence.durationMs}ms · dispositivo: ${navigator.userAgent.slice(0, 80)}`, mx, y); y += 10;
  doc.text('Documento generato e cifrato localmente (AES-GCM 256). L\'impronta SHA-256 è registrata nel registro consensi.', mx, y);
  return doc.output('arraybuffer');
};

export default function ConsentPage() {
  const { user } = useAuth();
  const brand = useBrand();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('trattamento_sanitario');
  const [patient, setPatient] = useState({ id: null, label: '' });
  const [taxCode, setTaxCode] = useState('');
  const [procedure, setProcedure] = useState('');
  const [sig, setSig] = useState({ isEmpty: true });
  const [confirmRead, setConfirmRead] = useState(false);
  const [backupToServer, setBackupToServer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [padKey, setPadKey] = useState(0);
  const [local, setLocal] = useState([]);
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(true);

  const tpl = useMemo(() => templates.find((t) => t.id === templateId) || templates[0], [templates, templateId]);
  const bodyText = useMemo(() => tpl ? fill(tpl.body, {
    patient_name: patient.label || '________', doctor_name: user?.full_name || '________',
    procedure: procedure || '________', clinic_name: brand.clinicName,
  }) : '', [tpl, patient.label, user?.full_name, procedure, brand.clinicName]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      try {
        const r = await getConsentTemplates();
        setTemplates(r.templates || []);
        setMeta(TEMPLATE_CACHE, r.templates).catch(() => {});
      } catch (e) {
        const cached = await getMeta(TEMPLATE_CACHE).catch(() => null);
        if (cached) setTemplates(cached); else toast.error('Modelli non disponibili offline');
      }
      const [l, r] = await Promise.all([
        getAll(STORES.CONSENTS).catch(() => []),
        listFeaEvidence().then((x) => x.items || []).catch(() => []),
      ]);
      setLocal(l.sort((a, b) => (a.signed_at < b.signed_at ? 1 : -1)));
      setRemote(r);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const canSign = !!tpl && patient.label.trim() && !sig.isEmpty && confirmRead;

  const signAndStore = async () => {
    if (!canSign) return;
    setSaving(true);
    try {
      const signedAt = new Date().toISOString();
      const pdf = buildConsentPdf({
        title: tpl.title, body: bodyText, patientName: patient.label.trim(), taxCode: taxCode.trim() || null,
        doctorName: user?.full_name, clinicName: brand.clinicName, procedure, signatureDataUrl: sig.dataUrl, signedAt, evidence: sig,
      });
      const docHash = await sha256Hex(pdf);
      const sigHash = await sha256Hex(sig.dataUrl || '');
      const key = await getDeviceKey();
      const encrypted = await aesEncrypt(key, pdf, docHash);
      const id = uuid();
      const rec = {
        id, template_id: tpl.id, template_title: tpl.title, patient_id: patient.id || null, patient_name: patient.label.trim(),
        patient_tax_code: taxCode.trim() || null, procedure: procedure || null, signed_at: signedAt, document_sha256: docHash,
        signature_sha256: sigHash, evidence: { strokes: sig.strokes, points: sig.points, duration_ms: sig.durationMs, avg_pressure: sig.avgPressure },
        encrypted, size_bytes: pdf.byteLength, sync_status: 'pending', created_at: signedAt, updated_at: signedAt,
      };
      await putRecord(STORES.CONSENTS, rec);
      await queueOutbox({
        kind: 'consent_fea', path: '/consents/fea',
        body: {
          template_id: tpl.id, template_title: tpl.title, patient_id: patient.id || null, patient_name: rec.patient_name,
          patient_tax_code: rec.patient_tax_code, procedure: rec.procedure, document_sha256: docHash, signature_sha256: sigHash,
          signed_at_client: signedAt, signature: rec.evidence, device_info: navigator.userAgent.slice(0, 280), local_record_id: id,
          encrypted_backup: backupToServer ? { alg: 'AES-GCM-256', iv: encrypted.iv, ciphertext: encrypted.ciphertext } : null,
        },
        ref_store: STORES.CONSENTS, ref_id: id,
      });
      toast.success('Consenso firmato, cifrato e archiviato sul dispositivo');
      setLocal((p) => [rec, ...p]);
      setPatient({ id: null, label: '' }); setTaxCode(''); setProcedure(''); setConfirmRead(false); setSig({ isEmpty: true }); setPadKey((k) => k + 1);
      syncEngine.flush().then(load);
    } catch (e) {
      toast.error(`Errore: ${e?.message || 'firma non riuscita'}`);
    } finally {
      setSaving(false);
    }
  };

  const downloadLocal = async (rec) => {
    try {
      const key = await getDeviceKey();
      const pdf = await aesDecrypt(key, rec.encrypted, rec.document_sha256);
      const h = await sha256Hex(pdf);
      if (h !== rec.document_sha256) { toast.error('Integrità compromessa: hash non corrispondente'); return; }
      downloadBuffer(pdf, `consenso-${rec.patient_name.replace(/\s+/g, '_')}-${rec.signed_at.slice(0, 10)}.pdf`);
      toast.success('PDF decifrato e verificato (SHA-256 OK)');
    } catch (e) {
      toast.error('Impossibile decifrare: chiave dispositivo diversa');
    }
  };

  const removeLocal = async (rec) => {
    if (!window.confirm('Eliminare la copia locale cifrata? L\'evidenza sul server resta.')) return;
    await deleteRecord(STORES.CONSENTS, rec.id);
    setLocal((p) => p.filter((x) => x.id !== rec.id));
  };

  if (user?.category === 'paziente') {
    return <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Sezione riservata agli operatori sanitari.</CardContent></Card>;
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="consent-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight flex items-center gap-2"><FileSignature className="h-6 w-6 text-primary" /> Consenso informato · FEA</h1>
          <p className="text-sm text-muted-foreground mt-1">Firma su tablet, PDF cifrato AES-256 sul dispositivo, impronta SHA-256 nel registro.</p>
        </div>
        <OfflineStatusBadge />
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Form */}
        <Card className="rounded-xl" data-testid="consent-form">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display">1 · Dati e modello</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loading && templates.length === 0 ? <Skeleton className="h-10 rounded-xl" /> : (
              <div>
                <Label>Modello di consenso</Label>
                <Select value={templateId} onValueChange={setTemplateId}>
                  <SelectTrigger className="h-11 rounded-xl" data-testid="consent-template-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Paziente *</Label>
              <PatientPicker endpoint="/voice-diary/patients" value={patient} onChange={setPatient} placeholder="Nome e cognome del paziente" testIdPrefix="consent-patient" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Codice fiscale</Label>
                <Input value={taxCode} onChange={(e) => setTaxCode(e.target.value.toUpperCase())} maxLength={16} className="h-11 rounded-xl font-mono uppercase" data-testid="consent-taxcode" />
              </div>
              <div>
                <Label>Prestazione</Label>
                <Input value={procedure} onChange={(e) => setProcedure(e.target.value)} placeholder="es. Infiltrazione ginocchio dx" className="h-11 rounded-xl" data-testid="consent-procedure" />
              </div>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/30 p-3 text-sm leading-relaxed max-h-56 overflow-y-auto scrollbar-thin" data-testid="consent-preview">
              <div className="font-semibold mb-1">{tpl?.title}</div>
              {bodyText}
            </div>
            <label className="flex items-start gap-3 rounded-xl border border-border/70 p-3 cursor-pointer touch-target">
              <Switch checked={confirmRead} onCheckedChange={setConfirmRead} data-testid="consent-confirm-read" />
              <span className="text-sm">Il paziente dichiara di aver letto e compreso il documento e di firmare liberamente.</span>
            </label>
          </CardContent>
        </Card>

        {/* Signature */}
        <Card className="rounded-xl" data-testid="consent-signature">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-display">2 · Firma del paziente</CardTitle>
            <CardDescription>Usa il dito o la penna sul tablet.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <SignaturePad key={padKey} height={220} onChange={setSig} disabled={saving} label="Firma del paziente" />
            {!sig.isEmpty && (
              <div className="text-xs text-muted-foreground flex items-center gap-3" data-testid="consent-evidence">
                <Fingerprint className="h-4 w-4 text-primary" /> {sig.strokes} tratti · {sig.points} punti · {(sig.durationMs / 1000).toFixed(1)} s
              </div>
            )}
            <label className="flex items-center justify-between gap-3 rounded-xl border border-border/70 p-3 cursor-pointer">
              <span className="text-sm flex items-center gap-2"><CloudUpload className="h-4 w-4 text-muted-foreground" /> Backup cifrato sul server <Badge variant="outline" className="text-[10px]">illeggibile senza chiave</Badge></span>
              <Switch checked={backupToServer} onCheckedChange={setBackupToServer} data-testid="consent-backup-toggle" />
            </label>
            <Button onClick={signAndStore} disabled={!canSign || saving} className="w-full h-12 rounded-xl text-base" data-testid="consent-sign-btn">
              {saving ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />} {saving ? 'Cifratura in corso…' : 'Firma, cifra e archivia'}
            </Button>
            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> Il PDF non lascia mai il dispositivo in chiaro. Al server viene inviata solo l'impronta SHA-256 con le evidenze FEA (DPCM 22/02/2013).</p>
          </CardContent>
        </Card>
      </div>

      {/* Archive */}
      <Card className="rounded-xl" data-testid="consent-archive">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base font-display">Archivio locale cifrato ({local.length})</CardTitle>
            <Badge variant="secondary" className="text-[10px]">Registro server: {remote.length} evidenze</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
            : local.length === 0 ? <p className="text-sm text-muted-foreground">Nessun consenso archiviato su questo dispositivo.</p>
              : (
                <ul className="space-y-2">
                  {local.map((r) => (
                    <li key={r.id} className="flex items-center gap-3 rounded-xl border border-border/70 p-3" data-testid={`consent-item-${r.id}`}>
                      <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0"><Lock className="h-5 w-5" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm truncate">{r.patient_name} · {r.template_title}</div>
                        <div className="text-xs text-muted-foreground truncate font-mono">SHA-256 {r.document_sha256.slice(0, 16)}… · {new Date(r.signed_at).toLocaleString('it-IT')} · {(r.size_bytes / 1024).toFixed(0)} KB</div>
                      </div>
                      {remote.some((x) => x.document_sha256 === r.document_sha256) || r.sync_status === 'synced'
                        ? <Badge variant="success" className="text-[10px] hidden sm:inline-flex"><CheckCircle2 className="h-3 w-3 mr-1" /> registrato</Badge>
                        : <Badge variant="outline" className="text-[10px] border-warning/50 text-warning hidden sm:inline-flex">in sync</Badge>}
                      <Button variant="outline" size="sm" onClick={() => downloadLocal(r)} className="touch-target" data-testid={`consent-download-${r.id}`}><Download className="h-4 w-4" /> PDF</Button>
                      <Button variant="ghost" size="icon" onClick={() => removeLocal(r)} aria-label="Elimina copia locale" data-testid={`consent-delete-${r.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </li>
                  ))}
                </ul>
              )}
        </CardContent>
      </Card>
    </div>
  );
}
